import React, { useEffect, useRef, useState } from "react";
import { EmbeddedCheckout } from "./EmbeddedCheckout";
import { confirmCheckoutSession } from "../utils/funnelCheckout";
import { logger } from "../utils/logger";

/**
 * The checkout screen the website wall and the Settings modal share, after a
 * plan's button: an order card (the plan, what is charged today, what it
 * renews at, a Change button back to the plans) over Stripe's Embedded
 * Checkout — exactly as the web funnel pays (components/funnel/FunnelCheckout).
 *
 * The session is created embedded (`ui_mode: embedded`) with the same body
 * the hosted flow sent (surface, arm, chosen price, win-back); the backend
 * prices it. When Stripe reports the payment complete, the page has the
 * backend confirm the session (owner-only read-back) and then lands on the
 * same return URL the hosted redirect used (`?upgraded=true&tier&billing&
 * session_id`), so everything after a purchase — the welcome toast, the
 * activation poll, the Google Ads conversion, Settings' own handler — is
 * unchanged. If the embedded form cannot load, the hosted page is the
 * fallback, as on the funnel.
 *
 * `summary`: { name, chip, today, renewal } — what the order card says, built
 * by the caller from the card it showed. `offer`: 'winback' or null.
 * `experiment`: { surface, offerVariant, chosenCents } or null. `surface`:
 * 'web_paywall' | 'subscription_modal'. `expect`: what the card promised —
 * { kind: 'normal' | 'winback' | 'trial', offerVariant, chosenCents } — checked
 * against what the backend actually priced the session as; on any difference
 * (a win-back offer consumed meanwhile, a trial just used up, an arm no
 * longer configured) the form is not shown and `onOfferChanged` sends the
 * parent back to freshly priced plans, so a card never promises what
 * checkout will not charge. `onBusyChange(true)` while a payment completes or
 * the hosted hand-off is under way, so the parent locks Back / Close.
 */

/** What the backend priced a session as, from its response. */
export const pricedKindOf = (data) => (
  Number(data?.trial_days || 0) > 0 ? "trial"
    : data?.intro_offer_variant === "winback_33_3mo" ? "winback"
      : "normal"
);

/** Whether the session the backend made matches the card the parent saw. */
export const sessionMatches = (data, expect) => {
  if (!expect?.kind) return true;
  if (pricedKindOf(data) !== expect.kind) return false;
  if (expect.kind !== "normal") return true;
  // a response that does not say (an older backend) cannot disagree
  if (expect.offerVariant && data && "offer_variant" in data && (data.offer_variant || null) !== expect.offerVariant) return false;
  if (Number.isInteger(expect.chosenCents) && data && "chosen_cents" in data && data.chosen_cents !== expect.chosenCents) return false;
  return true;
};

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

export const returnUrlFor = ({ returnPath, tier, billing, sessionId }) => {
  const rp = typeof returnPath === "string" && returnPath.startsWith("/") && !returnPath.startsWith("//") ? returnPath : "/settings";
  const sep = rp.includes("?") ? "&" : "?";
  return `${window.location.origin}${rp}${sep}upgraded=true&tier=${encodeURIComponent(tier)}&billing=${encodeURIComponent(billing)}&session_id=${encodeURIComponent(sessionId)}`;
};

export const MembershipCheckout = ({
  apiClient, user, tier, billing, summary, experiment = null, offer = null, surface = null, expect = null,
  publishableKey, returnPath, onBack, onOfferChanged = null, onBusyChange = null,
}) => {
  // 'creating' → 'ready' (the form is up) → 'paid' (Stripe said so; confirming and leaving)
  // 'hosted' (falling back to Stripe's page) · 'failed' (nothing could be started)
  const [phase, setPhase] = useState("creating");
  const [session, setSession] = useState(null);   // { sessionId, clientSecret }
  const [attempt, setAttempt] = useState(0);
  const leavingRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;   // (React's development double-mount unmounts and remounts us: stay honest either way)
    return () => { mountedRef.current = false; };
  }, []);
  // The session request in flight for the current attempt. React's development
  // double-mount runs the effect twice; the second run joins this request
  // instead of asking Stripe for a second session.
  const inflightRef = useRef(null);   // { attempt, promise }
  const headingRef = useRef(null);
  useEffect(() => { headingRef.current?.focus(); }, []);
  // the parent locks Back / Close while a payment completes or the hand-off runs
  const busy = phase === "paid" || phase === "hosted";
  useEffect(() => { onBusyChange?.(busy); }, [busy, onBusyChange]);

  const path = returnPath || `${window.location.pathname}${window.location.search}`;
  const apiTier = tier === "plus" ? "pro" : tier;
  const createSession = (embedded) => apiClient.createStripeCheckoutSession(user.id, user.email, apiTier, billing, path, offer, experiment, embedded, surface);

  // Stripe's hosted page: when the embedded form cannot be shown here
  const goHosted = async () => {
    if (leavingRef.current) return;
    leavingRef.current = true;
    setPhase("hosted");
    try {
      const r = await createSession(false);
      if (!mountedRef.current) return;   // the parent closed meanwhile: go nowhere
      if (r?.status && r.data?.checkout_url) {
        if (!sessionMatches(r.data, expect)) {
          onOfferChanged?.(pricedKindOf(r.data));
          return;
        }
        window.location.assign(r.data.checkout_url);
        return;
      }
      throw new Error(r?.error || "no checkout url");
    } catch (error) {
      logger.warn("Hosted checkout fallback failed:", error?.name || "Error");
      leavingRef.current = false;
      if (mountedRef.current) setPhase("failed");
    }
  };

  useEffect(() => {
    let cancelled = false;
    setPhase("creating");
    setSession(null);
    if (!publishableKey) {
      // the embedded form cannot mount without Stripe's publishable key: the hosted page, directly
      goHosted();
      return () => { cancelled = true; };
    }
    (async () => {
      try {
        if (inflightRef.current?.attempt !== attempt) inflightRef.current = { attempt, promise: createSession(true) };
        const r = await inflightRef.current.promise;
        if (cancelled) return;
        if (r?.status && r.data?.checkout_url && !r.data?.client_secret) {
          // an older backend answered a hosted session: use it
          window.location.assign(r.data.checkout_url);
        } else if (r?.status && r.data?.client_secret && r.data?.session_id) {
          if (!sessionMatches(r.data, expect)) {
            // the backend priced it differently from the card (the offer changed
            // between opening and tapping): never show a form for a different charge
            onOfferChanged?.(pricedKindOf(r.data));
            return;
          }
          setSession({ sessionId: r.data.session_id, clientSecret: r.data.client_secret });
          setPhase("ready");
        } else {
          throw new Error(r?.error || "no session");
        }
      } catch (error) {
        if (cancelled) return;
        logger.warn("Checkout session failed:", error?.name || "Error");
        setPhase("failed");
      }
    })();
    return () => {
      cancelled = true;
    };
    // a new attempt (Try again) re-creates the session; the inputs themselves are fixed for this screen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  const onComplete = async () => {
    if (leavingRef.current) return;
    leavingRef.current = true;
    setPhase("paid");
    // our own server reads the session back (owner-only); the landing page's
    // handler counts the conversion only for a session it can confirm too
    await confirmCheckoutSession(apiClient, session.sessionId);
    window.location.assign(returnUrlFor({ returnPath: path, tier: apiTier, billing, sessionId: session.sessionId }));
  };

  return (
    <div data-testid="membership-checkout" style={{ maxWidth: 480, margin: "0 auto", width: "100%" }}>
      <h3 ref={headingRef} tabIndex={-1} style={{ ...FREDOKA, fontSize: 24, fontWeight: 700, color: "#0B1A33", textAlign: "center", margin: "0 0 14px", outline: "none" }}>Almost there!</h3>

      {/* The order, in the plan cards' own style */}
      <div style={{ background: "#FFFFFF", border: "2px solid rgba(11,26,51,0.08)", borderRadius: 20, padding: "16px 18px", marginBottom: 16, boxShadow: "0px 2px 0px rgba(11,26,51,0.04)" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <strong style={{ ...FREDOKA, fontSize: 18, color: "#0065F8", textTransform: "uppercase" }}>{summary?.name}</strong>
              {summary?.chip && (
                <em style={{ ...FREDOKA, fontStyle: "normal", fontSize: 11.5, fontWeight: 700, color: "#0065F8", background: "#E8F2FF", borderRadius: 999, padding: "3px 9px", letterSpacing: "0.04em" }}>{summary.chip}</em>
              )}
            </span>
            <small style={{ display: "block", fontSize: 12.5, color: "#5A6B85", marginTop: 2 }}>Pixley {summary?.name} membership</small>
          </span>
          <button
            type="button"
            onClick={onBack}
            disabled={busy}
            style={{ ...FREDOKA, fontSize: 13.5, fontWeight: 700, color: "#0065F8", background: "none", border: "none", cursor: "pointer", padding: "2px 0" }}
          >
            Change
          </button>
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          <span style={{ ...FREDOKA, fontSize: 30, fontWeight: 700, color: "#0B1A33", lineHeight: 1 }}>{summary?.today}</span>
          {" "}
          <span style={{ fontSize: 13.5, color: "#334155", fontWeight: 600 }}>today</span>
        </div>
        <div style={{ fontSize: 12.5, color: "#5A6B85", marginTop: 6 }}>{summary?.renewal} · cancel anytime</div>
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontSize: 12.5, fontWeight: 600, color: "#334155", marginBottom: 12 }}>
        <svg width="14" height="14" fill="none" stroke="#0065F8" viewBox="0 0 24 24" strokeWidth={2} aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
        Secure checkout
      </div>

      {phase === "failed" ? (
        <div role="alert" style={{ textAlign: "center", padding: "22px 16px", background: "#FFFFFF", borderRadius: 20, border: "2px solid rgba(11,26,51,0.08)" }}>
          <p style={{ ...FREDOKA, fontSize: 16, fontWeight: 700, color: "#0B1A33", margin: "0 0 6px" }}>We couldn't start checkout just now</p>
          <p style={{ fontSize: 13, color: "#5A6B85", margin: "0 0 14px", lineHeight: 1.5 }}>Please check your connection and try again in a moment.</p>
          <button
            type="button"
            onClick={() => { leavingRef.current = false; setAttempt((n) => n + 1); }}
            style={{ ...FREDOKA, padding: "10px 24px", borderRadius: 14, fontSize: 14, fontWeight: 700, cursor: "pointer", color: "#FFFFFF", background: "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)", border: "1px solid rgba(255,255,255,0.5)", boxShadow: "0px 4px 0px #073B8E" }}
          >
            Try again
          </button>
        </div>
      ) : phase === "paid" ? (
        <p role="status" style={{ ...FREDOKA, fontSize: 16, fontWeight: 700, color: "#0065F8", textAlign: "center", margin: "28px 0" }}>Payment received — setting up your membership…</p>
      ) : phase === "hosted" ? (
        <p role="status" style={{ fontSize: 13.5, color: "#5A6B85", textAlign: "center", margin: "24px 0" }}>Taking you to Stripe's secure page…</p>
      ) : phase === "ready" ? (
        <EmbeddedCheckout clientSecret={session.clientSecret} publishableKey={publishableKey} onComplete={onComplete} onUnavailable={goHosted} />
      ) : (
        <div role="status" aria-label="Starting secure checkout" style={{ minHeight: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span aria-hidden="true" style={{ display: "inline-block", width: 24, height: 24, borderRadius: "50%", border: "3px solid #D7E3F3", borderTopColor: "#0065F8", animation: "tw-spin 0.9s linear infinite" }} />
        </div>
      )}
    </div>
  );
};

export default MembershipCheckout;
